import { Add as AddIcon, Person as UserIcon } from '@mui/icons-material';
import { Button, Paper } from '@mui/material';
import ModalUsersCreation from 'Components/ModalUsersCreation';
import EmptyState from 'Components/Shared/EmptyState';
import { PersonContext } from 'Context/CurrentPerson';
import useTranslate from 'Hooks/useTranslate';
import { ReactNode, useContext, useState } from 'react';
import TestIds from 'Tests/TestIds';
import TableCollection from 'Theme/components/TableCollection/TableCollection';
import TableCollectionColumnSelection from 'Theme/components/TableCollection/TableCollectionColumnSelection';
import { MAX_SELECTED } from 'Theme/config';
import ExpandedSearch from '../../../Components/Shared/ExpandedSearch';
import ActionOnSelectedUsers from '../ActionOnSelectedUsers';
import { useStyles } from './UsersTable.styles';

export interface UsersTableBaseProps<TOrdering extends string = string> {
  loading: boolean;
  rows: any[];
  selectedCount: number;
  selectedIds: string[] | null;
  totalCount: number;
  orderBy: TOrdering;
  rowsPerPage: number;
  currentPage: number;
  onReloadUsers: any;
  filter: string | null;
  onSearch: (search: string) => void;
  onChangeOrderBy: (orderBy: TOrdering) => void;
  onChangeRowsPerPage: (newRowsPerPage: number) => void;
  onSelect: (ids: string[] | null) => void;
  onUnselect: (ids: string[] | null) => void;
  onChangePage: (newPage: number) => void;

  activeColumns: any[];
  onChangeColumns: (newColumns: any) => void;
  onResetColumns: () => void;

  toolbarLeftContent?: ReactNode;
  canDeleteUsers: boolean;
  canSubscribeUsers: boolean;
  canInviteUsers: boolean;
  organizationCode?: string;
}

function UsersTableBase<TOrdering extends string = string>(props: Readonly<UsersTableBaseProps<TOrdering>>) {
  const {
    totalCount,
    selectedCount,
    loading,
    rows,
    filter,
    orderBy,
    rowsPerPage,
    currentPage,
    selectedIds,
    onReloadUsers,
    organizationCode,
    onSelect,
    onUnselect,
    onSearch,
    onChangeOrderBy,
    onChangeRowsPerPage,
    onChangePage,
    activeColumns,
    onChangeColumns,
    onResetColumns,
    toolbarLeftContent,
    canDeleteUsers,
    canSubscribeUsers,
    canInviteUsers
  } = props;

  const classes = useStyles();
  const { currentPersonRoles } = useContext(PersonContext);
  const ts = useTranslate();

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const showInviteButton = (currentPersonRoles.isAccountAdmin || currentPersonRoles.canWriteAll) && canInviteUsers;

  return <>
    <Paper className={classes.root}>
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          {toolbarLeftContent}
        </div>
        <div className={classes.toolbarRight}>
          {showInviteButton && (
              <Button
                color="primary"
                onClick={() => setModalIsOpen(!modalIsOpen)}
                data-testid={TestIds.pages.users.inviteUserButton}
              >
                <AddIcon
                  className={classes.addUserButtonIcon}
                  data-testid={TestIds.pages.users.inviteUserIcon}
                />
                {ts('page.users.action.addUser')}
              </Button>
            )}
          <TableCollectionColumnSelection
            columns={activeColumns}
            onChange={onChangeColumns}
            onReset={onResetColumns}
          />
        </div>
      </div>
      <ExpandedSearch
        onChange={onSearch}
        testId={TestIds.pages.users.searchUserInput + 'UsersTable'}
        placeholder={ts('component.searchBars.placeholder.users')}
      />
      <TableCollection
        loading={loading}
        columns={activeColumns}
        rows={rows}
        getRowId={({ node: person }: { node: { immutableId: string } }) =>
          person?.immutableId
        }
        orderBy={orderBy}
        onChangeOrderBy={onChangeOrderBy as (orderBy: string) => void}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        page={currentPage}
        onChangePage={onChangePage}
        selectedIds={selectedIds}
        onUnselect={onUnselect}
        onSelect={onSelect}
        maxSelected={MAX_SELECTED}
        totalCount={totalCount}
        isSelectAllDisabled={false}
        messages={{
          maxSelectedExceeded: 'page.users.maxLimitExceeded',
          pageAllSelected: 'page.users.pageSelectedUserCount',
          selectAll: 'page.users.selectUserCount'
        }}
        emptyStateData={
          <EmptyState
            icon={<UserIcon />}
            primary={ts('page.users.table.emptyStatePrimary')}
          />
        }
      />
    </Paper>

    <ActionOnSelectedUsers
      selectedIds={selectedIds ?? []}
      selectAllEnabled={selectedIds == null}
      selectedCount={selectedCount}
      onReloadUsers={onReloadUsers}
      disabled={selectedCount > MAX_SELECTED}
      searchTerm={filter ?? ''}
      onSelect={onSelect}
      totalCount={totalCount}
      loading={loading}
      canDeleteUsers={canDeleteUsers}
      canSubscribeUsers={canSubscribeUsers}
      organizationCode={organizationCode}
    />
    {modalIsOpen && (
      <ModalUsersCreation
        isOpen={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
      />
    )}
  </>;
}

export default UsersTableBase;
